import React, { useEffect, useMemo, useState } from "react";
import "./FrontPage.css";
import headerImage from "./assets/images/Header.png";
import mobileHeaderImage from "./assets/images/Fontego_HvitHorizontal.png";
import { assetUrl, fetchJSON } from "../../utils/api";

const DEFAULT_HOME_CONFIG = {
  imageLayers: [],
  backgroundColor: "#000000",
  fogEnabled: true,
  fogOpacity: 0.42,
  fogVelocity: 1,
  fogDensity: 1,
  fogColorPrimary: "#9bc4de",
  fogColorSecondary: "#8aaec7",
};

const clampOpacity = (value, fallback) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(0, Math.min(1, num));
};

const clampRange = (value, fallback, min, max) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(min, Math.min(max, num));
};

const normalizeHexColor = (value, fallback) => {
  const color = String(value || "").trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) return color;
  return fallback;
};

const hexToRgbTriplet = (hex, fallback = "#9bc4de") => {
  const normalized = normalizeHexColor(hex, fallback).replace("#", "");
  const expanded = normalized.length === 3
    ? normalized.split("").map((ch) => `${ch}${ch}`).join("")
    : normalized;
  const int = parseInt(expanded, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r}, ${g}, ${b}`;
};

const normalizeSize = (value, fallback = "L") => {
  const size = String(value || "").toUpperCase();
  return ["L", "M", "S"].includes(size) ? size : fallback;
};

const normalizeLayers = (layers, legacyPath = "", legacySize = "L", legacyOpacity = 1) => {
  const source = Array.isArray(layers) ? layers : [];
  const normalized = source
    .map((layer, index) => ({
      id: String(layer?.id || layer?._id || `layer-${index}`),
      imagePath: String(layer?.imagePath || "").trim(),
      imageSize: normalizeSize(layer?.imageSize, "L"),
      imageOpacity: clampOpacity(layer?.imageOpacity, 1),
    }))
    .filter((layer) => layer.imagePath);

  if (normalized.length > 0) return normalized;

  const path = String(legacyPath || "").trim();
  if (!path) return [];

  return [{
    id: "legacy-layer",
    imagePath: path,
    imageSize: normalizeSize(legacySize, "L"),
    imageOpacity: clampOpacity(legacyOpacity, 1),
  }];
};

const normalizeHomeConfig = (value) => {
  if (!value || typeof value !== "object") return DEFAULT_HOME_CONFIG;
  return {
    imageLayers: normalizeLayers(value.imageLayers, value.imagePath, value.imageSize, value.imageOpacity),
    backgroundColor: String(value.backgroundColor || "#000000"),
    fogEnabled: Boolean(value.fogEnabled),
    fogOpacity: clampOpacity(value.fogOpacity, 0.42),
    fogVelocity: clampRange(value.fogVelocity, 1, 0.2, 3),
    fogDensity: clampRange(value.fogDensity, 1, 0, 2),
    fogColorPrimary: normalizeHexColor(value.fogColorPrimary, "#9bc4de"),
    fogColorSecondary: normalizeHexColor(value.fogColorSecondary, "#8aaec7"),
  };
};

const FrontPage = () => {
  const [homeConfig, setHomeConfig] = useState(DEFAULT_HOME_CONFIG);
  const [configReady, setConfigReady] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const [fogIntroProgress, setFogIntroProgress] = useState(0);

  useEffect(() => {
    document.body.classList.add("frontpage-no-scroll");
    document.documentElement.classList.add("frontpage-no-scroll");

    return () => {
      document.body.classList.remove("frontpage-no-scroll");
      document.documentElement.classList.remove("frontpage-no-scroll");
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadHomeConfig = async () => {
      try {
        const payload = await fetchJSON("/api/forside");
        if (!active) return;
        setHomeConfig(normalizeHomeConfig(payload));
      } catch {
        if (!active) return;
        setHomeConfig(DEFAULT_HOME_CONFIG);
      } finally {
        if (!active) return;
        setConfigReady(true);
      }
    };
    loadHomeConfig();
    return () => {
      active = false;
    };
  }, []);

  const layerSources = useMemo(() => (
    (Array.isArray(homeConfig.imageLayers) ? homeConfig.imageLayers : [])
      .map((layer, index) => ({
        ...layer,
        id: layer?.id || `layer-${index}`,
        src: assetUrl(layer.imagePath),
      }))
      .filter((layer) => layer.src)
  ), [homeConfig.imageLayers]);

  const fallbackSource = useMemo(() => {
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 768px)").matches) {
      return mobileHeaderImage;
    }
    return headerImage;
  }, []);

  const preloadSources = useMemo(() => {
    if (layerSources.length > 0) {
      return layerSources.map((layer) => String(layer.src || "").trim()).filter(Boolean);
    }
    return [fallbackSource];
  }, [layerSources, fallbackSource]);

  useEffect(() => {
    if (!configReady) return undefined;

    setImagesReady(false);
    const uniqueSources = Array.from(new Set(preloadSources.map((src) => String(src || "").trim()).filter(Boolean)));
    if (!uniqueSources.length) {
      setImagesReady(true);
      return undefined;
    }

    let cancelled = false;
    let pending = uniqueSources.length;
    const releaseOne = () => {
      pending -= 1;
      if (!cancelled && pending <= 0) {
        setImagesReady(true);
      }
    };

    const loaders = uniqueSources.map((src) => {
      const img = new Image();
      img.onload = releaseOne;
      img.onerror = releaseOne;
      img.src = src;
      return img;
    });

    return () => {
      cancelled = true;
      loaders.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [configReady, preloadSources]);

  useEffect(() => {
    if (!configReady) return undefined;

    setFogIntroProgress(0);
    let rafId = 0;
    let startTime = 0;
    const durationMs = 1800;

    const tick = (now) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.max(0, Math.min(1, elapsed / durationMs));
      setFogIntroProgress(progress);
      if (progress < 1) {
        rafId = window.requestAnimationFrame(tick);
      }
    };

    rafId = window.requestAnimationFrame(tick);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [configReady, preloadSources]);

  const fogDensityTarget = clampRange(homeConfig.fogDensity, 1, 0, 2);
  const fogVelocityTarget = clampRange(homeConfig.fogVelocity, 1, 0.2, 3);
  const fogOpacityTarget = clampOpacity(homeConfig.fogOpacity, 0.42) * fogDensityTarget;
  const fogIntro = clampRange(fogIntroProgress, 0, 0, 1);
  const fogDensity = fogDensityTarget * fogIntro;
  const fogVelocity = Math.max(0.08, fogVelocityTarget * fogIntro);
  const fogOpacity = fogOpacityTarget * fogIntro;

  return (
    <main className="front-page" aria-label="Front page background" style={{ backgroundColor: homeConfig.backgroundColor }}>
      <div className="front-page-bg" style={{ backgroundColor: homeConfig.backgroundColor }} />
      {homeConfig.fogEnabled && (
        <div
          className="front-page-fog"
          style={{
            opacity: fogOpacity,
            "--fog-speed": fogVelocity,
            "--fog-density": Math.max(0.02, fogDensity),
            "--fog-intro": fogIntro,
            "--fog-velocity-target": fogVelocityTarget,
            "--fog-color-primary-rgb": hexToRgbTriplet(homeConfig.fogColorPrimary, "#9bc4de"),
            "--fog-color-secondary-rgb": hexToRgbTriplet(homeConfig.fogColorSecondary, "#8aaec7"),
          }}
          aria-hidden="true"
        >
          <span className="front-page-fog__layer front-page-fog__layer--a" />
          <span className="front-page-fog__layer front-page-fog__layer--b" />
          <span className="front-page-fog__layer front-page-fog__layer--c" />
        </div>
      )}
      <div className={`front-page-media ${imagesReady ? "is-ready" : "is-loading"}`}>
        {layerSources.length > 0 ? (
          layerSources.map((layer, index) => (
            <div
              key={layer.id || `${layer.src}-${index}`}
              className={`front-page-layer front-page-layer--${normalizeSize(layer.imageSize, "L")}`}
              style={{ zIndex: 2 + index }}
            >
              <img
                src={layer.src}
                alt="Dødsego frontpage"
                className={`front-page-image front-page-image--${normalizeSize(layer.imageSize, "L")}`}
                style={{ opacity: clampOpacity(layer.imageOpacity, 1) }}
              />
            </div>
          ))
        ) : (
          <div className="front-page-layer front-page-layer--L" style={{ zIndex: 2 }}>
            <picture>
              <source media="(max-width: 768px)" srcSet={mobileHeaderImage} />
              <img src={headerImage} alt="Dødsego frontpage" className="front-page-image front-page-image--L" style={{ opacity: 1 }} />
            </picture>
          </div>
        )}
      </div>
      {!imagesReady && <div className="front-page-loading-curtain" aria-hidden="true" />}
    </main>
  );
};

export default FrontPage;